import { Component, EventEmitter, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, ModalService, UserService } from '@cfp/core';
import {
  Alert,
  Button,
  FormGroupActionsDirective,
  FormGroupAfterLabelDirective,
  FormGroupComponent,
  InputPassword,
  InputText,
  Link,
  ModalContent,
  ValidationFeedback
} from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ForgotPasswordModal } from '../forgot-password-modal';
import { LoginMessages } from '../login-messages';

@Component({
  selector: 'lib-login-modal',
  templateUrl: './login-modal.html',
  styleUrls: ['./login-modal.scss'],
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    Link,
    FormGroupComponent,
    InputText,
    Button,
    InputPassword,
    FormGroupActionsDirective,
    LoginMessages,
    FormGroupAfterLabelDirective,
    ModalContent,
    Alert,
    ValidationFeedback
  ]
})
export class LoginModal implements OnInit {
  public createAccount: EventEmitter<void> = new EventEmitter();
  public ssoLoginUrl?: string;
  public showLoginUsingSso: boolean = false;
  public forceSsoWhenEmailContains?: string;

  protected loading: boolean = false;
  protected form: FormGroup;
  protected warningMessages: string[] = [];
  protected errorMessages: string[] = [];
  protected changePassword: boolean = false;
  protected loginMessagesDataTest?: string;
  protected shouldLoginUsingSsoOnly: boolean = false;

  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly bsModalRef = inject(BsModalRef);
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);

  public ngOnInit(): void {
    let group: any = {
      username: ['', Validators.required],
      password: ['', Validators.required]
    };

    this.form = this.formBuilder.group(group);

    const forceSsoWhenEmailContains = this.forceSsoWhenEmailContains;
    if (forceSsoWhenEmailContains) {
      this.form.controls['username'].valueChanges.subscribe((value: string) => {
        this.shouldLoginUsingSsoOnly = value?.indexOf(forceSsoWhenEmailContains) >= 0;
        if (this.shouldLoginUsingSsoOnly) {
          document.getElementById('loginSso')?.focus();
        }
      });
    }

    setTimeout(() => {
      document.getElementById('username')?.focus();
    });
  }

  protected isFieldValid(field: string): boolean {
    return this.form.get(field)?.valid ?? false;
  }

  protected hide(): void {
    this.bsModalRef.hide();
  }

  protected createAccountClick(): void {
    this.createAccount.emit();
    this.hide();
  }

  protected showForgotPassword(): void {
    this.hide();
    setTimeout(() => {
      this.modalService.show(ForgotPasswordModal, {
        class: 'modal-sm'
      });
    });
  }

  protected submitForm(): void {
    this.loading = true;
    this.warningMessages = [];
    this.errorMessages = [];
    this.authService.login(this.form.value.username, this.form.value.password).subscribe({
      next: () => {
        this.authService.goToApp(this.route.snapshot);
        this.hide();
      },
      error: (error) => {
        this.loading = false;
        this.errorMessages = [];
        this.warningMessages = [];
        this.loginMessagesDataTest = undefined;
        this.userService.changePasswordUserName.set(undefined);
        this.changePassword = false;

        if (error.message === 'changePassword') {
          this.warningMessages = ['login.error.explainChange'];
          this.loginMessagesDataTest = 'warningChangePassword';
          this.userService.changePasswordUserName.set(this.form.value.username);
          this.changePassword = true;
        } else if (error.status == 504 || error.status == 503) {
          this.errorMessages = ['login.error.serviceUnavailable'];
          this.loginMessagesDataTest = 'errorServiceUnavailable';
        } else {
          this.errorMessages = ['login.error.passwordNotCorrect', 'login.error.passwordNotCorrectExpires'];
          this.loginMessagesDataTest = 'errorPasswordNotCorrect';
        }
      }
    });
  }

  protected routePasswordChange(): void {
    this.changePassword = false;
    this.hide();
    this.router.navigateByUrl('/login/auth/change');
  }
}
